import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios to always include the Bearer token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Global flag to prevent multiple simultaneous session-expired redirects
let isRedirectingToLogin = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  // Keep ref in sync so interceptor can check user state
  useEffect(() => { userRef.current = user; }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      return true;
    } catch (error) {
      setUser(null);
      // If auth fails, clear stale token
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Global 401 interceptor — handles session expiry across the ENTIRE app
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If a non-auth API call gets 401, the session has expired
        if (
          error.response?.status === 401 &&
          !error.config?.url?.includes('/auth/') &&
          userRef.current &&
          !isRedirectingToLogin
        ) {
          isRedirectingToLogin = true;
          localStorage.removeItem('session_token');
          setUser(null);
          error.isSessionExpired = true;
          // Show toast instead of hard redirect — let the user finish what they're doing
          // The app will show the landing page since user is null
          setTimeout(() => { isRedirectingToLogin = false; }, 5000);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
